import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Trash2, Plus } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

interface ClientTaskItem {
    id: string;
    case_id?: string;
    title: string;
    description?: string;
    due_date?: string;
    status: "pending" | "completed";
    case_number?: string;
}

export const ClientTasks = ({ client }: { client: any }) => {
    const { processes, addTask, updateClient } = useDespachos();
    const [tasks, setTasks] = useState<ClientTaskItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState("");

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // 1. Obtener los datos del address serializado
    let extraData: any = {};
    if (client.address) {
        try {
            extraData = JSON.parse(client.address);
        } catch (e) {
            extraData = {};
        }
    }
    const jsonTasks: ClientTaskItem[] = extraData.tasks || [];

    // 2. Obtener los expedientes de este cliente
    const clientProcesses = processes.filter(
        p => (p.client_name && p.client_name.trim().toLowerCase() === client.name?.trim().toLowerCase()) ||
             (p.metadata && p.metadata.client_id === client.id)
    );
    const clientProcessIds = clientProcesses.map(p => p.id);

    const fetchTasks = async () => {
        setLoading(true);
        let dbTasks: ClientTaskItem[] = [];

        if (clientProcessIds.length > 0) {
            try {
                const { data, error } = await supabase
                    .from("legal_tasks")
                    .select("*")
                    .in("case_id", clientProcessIds)
                    .order("due_date", { ascending: true });

                if (!error && data) {
                    dbTasks = data.map(t => {
                        const rel = clientProcesses.find(p => p.id === t.case_id);
                        return {
                            id: t.id,
                            case_id: t.case_id,
                            title: t.title,
                            description: t.description,
                            due_date: t.due_date,
                            status: t.status as "pending" | "completed",
                            case_number: rel?.case_number || "Expediente"
                        };
                    });
                }
            } catch (e: any) {
                console.error("Error fetching client tasks:", e);
            }
        }

        // Fusionar DB + JSON
        const mergedMap = new Map();
        dbTasks.forEach(item => mergedMap.set(item.id, item));
        jsonTasks.forEach(item => {
            if (!mergedMap.has(item.id)) {
                mergedMap.set(item.id, item);
            }
        });

        setTasks(Array.from(mergedMap.values()));
        setLoading(false);
    };

    useEffect(() => {
        fetchTasks();
    }, [processes, client.id]);

    const handleCreateTask = async () => {
        if (!title.trim()) return;

        const newTask: ClientTaskItem = {
            id: Math.random().toString(36).substring(2, 9),
            title: title.trim(),
            description: description.trim(),
            due_date: dueDate || undefined,
            status: "pending",
            case_number: clientProcesses[0]?.case_number || "Directo Cliente"
        };

        // Si tiene expediente, registrar en DB vía hook
        if (clientProcesses.length > 0) {
            try {
                await addTask({
                    case_id: clientProcesses[0].id,
                    title: title.trim(),
                    description: description.trim(),
                    due_date: dueDate || undefined,
                    status: "pending"
                });
            } catch (e) {
                console.error("Error creating DB task:", e);
            }
        }

        // Persistir en JSON dentro de client.address
        const updatedExtra = {
            ...extraData,
            tasks: [...jsonTasks, newTask]
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        setTitle("");
        setDescription("");
        setDueDate("");
        setIsOpen(false);
        toast.success("Tarea registrada correctamente");
        fetchTasks();
    };

    const handleToggleStatus = async (taskId: string, currentStatus: string) => {
        const newStatus: "pending" | "completed" = currentStatus === "completed" ? "pending" : "completed";

        // Actualizar en DB si existe
        try {
            await supabase
                .from("legal_tasks")
                .update({ status: newStatus })
                .eq("id", taskId);
        } catch (e) {}

        // Actualizar en JSON local
        const updatedJsonTasks = jsonTasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t);
        const updatedExtra = {
            ...extraData,
            tasks: updatedJsonTasks
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
        toast.success(newStatus === "completed" ? "Tarea completada" : "Tarea marcada como pendiente");
    };

    const handleDeleteTask = async (taskId: string) => {
        // Eliminar en DB si existe
        try {
            await supabase
                .from("legal_tasks")
                .delete()
                .eq("id", taskId);
        } catch (e) {}

        // Eliminar en JSON local
        const updatedJsonTasks = jsonTasks.filter(t => t.id !== taskId);
        const updatedExtra = {
            ...extraData,
            tasks: updatedJsonTasks
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        setTasks(prev => prev.filter(t => t.id !== taskId));
        toast.success("Tarea eliminada");
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Tareas del Cliente</CardTitle>
                    <p className="text-xs text-gray-400">Tareas y plazos pendientes de los expedientes asociados a {client.name}</p>
                </div>
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Agregar Tarea
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Nueva Tarea del Cliente</DialogTitle>
                            <p className="text-xs text-gray-400">Registra un plazo o compromiso pendiente</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Título de la Tarea *</Label>
                                <Input className="rounded-xl" placeholder="Ej: Solicitar copias de expediente" value={title} onChange={e => setTitle(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Descripción</Label>
                                <Input className="rounded-xl" placeholder="Detalles adicionales..." value={description} onChange={e => setDescription(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha Límite</Label>
                                <Input type="date" className="rounded-xl" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsOpen(false)}>Cancelar</Button>
                            <Button className="rounded-xl bg-legal-blue" onClick={handleCreateTask} disabled={!title.trim()}>Crear Tarea</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {loading ? (
                    <div className="text-center py-12 text-gray-400 text-sm">Cargando tareas...</div>
                ) : tasks.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="w-10"></TableHead>
                                <TableHead className="font-semibold text-gray-500">Referencia</TableHead>
                                <TableHead className="font-semibold text-gray-500">Tarea / Descripción</TableHead>
                                <TableHead className="font-semibold text-gray-500">Fecha Límite</TableHead>
                                <TableHead className="font-semibold text-gray-500">Estado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acción</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tasks.map((t) => (
                                <TableRow key={t.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell>
                                        <Checkbox
                                            checked={t.status === "completed"}
                                            onCheckedChange={() => handleToggleStatus(t.id, t.status)}
                                            className="rounded-md"
                                        />
                                    </TableCell>
                                    <TableCell className="font-bold text-gray-800 text-xs">
                                        {t.case_number || "Cliente"}
                                    </TableCell>
                                    <TableCell className="py-3">
                                        <p className={`font-semibold text-xs text-gray-950 ${t.status === "completed" ? "line-through text-gray-400" : ""}`}>
                                            {t.title}
                                        </p>
                                        {t.description && (
                                            <p className="text-[11px] text-gray-400 mt-0.5 max-w-[250px] truncate">
                                                {(t.description || "").replace("[ADELANTO]", "").replace(/responsable=[^\n]+/, "").trim()}
                                            </p>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-gray-500 text-xs font-semibold">
                                        {t.due_date ? new Date(t.due_date).toLocaleDateString() : "Sin fecha"}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={`text-[10px] font-bold py-0.5 px-2 rounded-full border ${
                                            t.status === "completed"
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : "bg-amber-50 text-amber-700 border-amber-200"
                                        }`}>
                                            {t.status === "completed" ? "Completado" : "Pendiente"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeleteTask(t.id)}
                                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <CheckCircle2 className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay tareas o plazos pendientes para este cliente.</p>
                        <p className="text-xs text-gray-300 mt-1">Usa el botón superior para agregar una tarea para este cliente.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
